import { ng } from '@uirouter/angularjs/lib/angular';
import { StateService, TransitionService } from '@uirouter/core';
import * as angular from 'angular';
import { IPopupSettings } from '../../../../app.common/modules/common/models';
import { NotificationService } from '../../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../../app.common/modules/common/services/popup.service';
import { AccountService } from '../../account/account.service';
import { RetailerService } from '../../api/retailer.service';
import { PlaceListShortModel } from '../../core';
import { MarketPlaceService } from '../marketplace.service';
import { IImageCroppedState } from '../models/IImageCroppedState';
import { IImageError } from '../models/IImageError';
import { IMarketPlaceRetailerAdvert } from '../models/IMarketPlaceRetailerAdvert';
import { IAdvertShort, IRetailerAdvert } from '../models/IRetailerAdvert';

export class AdForMarketplaceController implements ng.IOnInit, ng.IPostLink {

    public static $inject = [
        '$translate',
        'notificationService',
        'marketPlaceService',
        '$uibModal',
        'accountService',
        'popupService',
        'retailerService',
        '$transitions',
        '$state'];

    public advertForm: ng.IFormController & {
        location: ng.INgModelController,
        image: ng.INgModelController
    };

    public model: IMarketPlaceRetailerAdvert = null;
    public backupModel: IMarketPlaceRetailerAdvert;
    public backupImage: IImageCroppedState = {
        src: null as string,
        data: null,
        loading: false,
        valid: false,
        dirty: false,
        id: null,
        serverImgLength: null
    };

    public waiting: boolean = false;
    public firstImgLoad: boolean = true;

    public image: IImageCroppedState = {
        src: null as string,
        data: null,
        loading: false,
        valid: false,
        dirty: false,
        id: null,
        serverImgLength: null
    };

    public imageMinSize: ISize = {
        w: 640,
        h: 360
    };

    public imageOptions = { ratio: '16:9'};

    public error: IImageError = {
        imageMinSize: false,
        fileType: false,
        imageFileSizeMinSize: false,
        fileSize: false,
        imageRequired: false,
        imageMinAreaSize: false
    };

    public firstDiscardChangeRequest: boolean = true;

    constructor(
        public $translate: angular.translate.ITranslateService,
        public notificationService: NotificationService,
        public marketPlaceService: MarketPlaceService,
        public $uibModal: ng.ui.bootstrap.IModalService,
        public accountService: AccountService,
        public popupService: PopupService,
        public retailerService: RetailerService,
        public $transitions: TransitionService,
        public $state: StateService
    ) {
        $transitions.onBefore({}, (transition) => {
            const toStateName = transition.to().name;
            if (this.firstDiscardChangeRequest && this.pageHasChanges()) {
                return this.openDiscardChangesDialog()
                    .then(() => {
                        this.firstDiscardChangeRequest = false;
                        return transition.router.stateService.target(toStateName);
                    }, () => {
                        transition.abort();
                    });
            }
        });
    }

    public async $onInit() {
        this.loadAdvert();
    }

    public $postLink() {
        this.advertForm.location.$validators.locationModel = this.validateLocationModel.bind(this);
        this.advertForm.image.$validators.imageModel = this.validateImageModel.bind(this);
    }

    public async createOrUpdateRetailerAdverts() {
        if (!this.isValidModel()) {
            return;
        }

        try {
            this.waiting = true;
            const model: IAdvertShort = {
                PlaceID: this.model.selectedLocation.ID,
                ImageID: this.model.retailerAdvert.Advert.ImageID
            };
            if (this.model.retailerAdvert.Advert.ID) {
                await this.marketPlaceService.updateAdvert(this.model.retailerAdvert.Advert.ID, model, this.image.data);
            } else {
                await this.marketPlaceService.createAdvert(model, this.image.data);
            }
            await this.loadAdvert();
            this.advertForm.$setPristine();
            this.firstImgLoad = true;
            this.model.isNew = false;
            this.notificationService.showSuccessMessage();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        } finally {
            this.waiting = false;
            this.notificationService.clearAll();
        }
    }

    public pageHasChanges() {

        if (!this.model && !this.backupModel) {
            return false;
        }

        return this.backupModel.selectedLocation.ID !== this.model.selectedLocation.ID ||
            this.backupModel.linkOption !== this.model.linkOption ||
            this.backupImage && this.image && this.backupImage.src !== this.image.src;
    }

    public async openDiscardChangesDialog() {
        return this.popupService.confirm({
                cancelClass: 'btn btn-with-background-icon icon-check--white btn-success',
                cancelTxt: this.$translate.instant('discard_continue'),
                okClass: 'btn btn-with-background-icon close-icon--tiny24',
                okTxt: this.$translate.instant('discard_cancel'),
                title: this.$translate.instant('discard_changes_title'),
                content: this.$translate.instant('discard_changes_promt'),
                invertAction: true,
            } as IPopupSettings);
    }

    public async openSelectLocationDialog() {
        const operationResult: any = await this.$uibModal.open({
            component: 'selectSingleLocation',
            resolve: { options: () => {
                    return {
                        selectedLocationID: this.model.selectedLocation ? this.model.selectedLocation.ID : null,
                    };
                } },
            size: 'md',
            backdrop: 'static',
            windowClass: 'modal-design-v2'
        }).result;

        if (operationResult.status === 'ok') {
            this.model.selectedLocation = {
                ID: operationResult.selectedLocation.ID,
                Name: operationResult.selectedLocation.Name,
                Address: operationResult.selectedLocation.Address,
                Active: operationResult.selectedLocation.Active,
            };
            this.model.retailerAdvert.Advert.PlaceID = operationResult.selectedLocation.ID;
            this.advertForm.location.$validate();
        }
    }

    public changeLinkOption(linkOption: number) {
        if (linkOption === 0 && this.model.selectedLocation) {
            this.model.selectedLocation.ID = null;
            this.model.selectedLocation.Name = null;
            this.model.selectedLocation.Active = null;
            this.model.selectedLocation.Address = null;
            this.model.retailerAdvert.Advert.PlaceID = null;
            this.model.retailerAdvert.Advert.Place = null;
        }
    }

    public async openDeleteDialog() {
        try {
            await this.popupService.confirm({
                okTxt: this.$translate.instant('ad_retailer_delete_dialog_content_cancel'),
                okClass: 'btn btn-with-background-icon close-icon--tiny24',
                cancelTxt: this.$translate.instant('ad_retailer_delete_dialog_content_ok'),
                cancelClass: 'btn btn-with-background-icon icon-check--white btn-success',
                title: this.$translate.instant('ad_retailer_delete_dialog_title'),
                content: this.$translate.instant('ad_retailer_delete_dialog_content'),
                invertAction: true,
            } as IPopupSettings);
        } catch (error) {
            return;
        }

        this.notificationService.showWaitingMessage();
        this.waiting = true;
        try {
            await this.marketPlaceService.deleteRetailerAdverts(this.model.retailerAdvert.Advert.ID);
            this.notificationService.clearAll();
            this.model = this.getDefaultAdvertModel();
            this.image = {
                src: null as string,
                data: null,
                loading: false,
                valid: false,
                dirty: false,
                id: null,
                serverImgLength: null
            };
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    public imageOnload(event: Event) {
        if (this.firstImgLoad && !this.model.isNew) {
            this.advertForm.$setPristine();
            this.firstImgLoad = false;
        }
    }

    private isValidModel() {
        return this.image.data && (this.model.linkOption === 0 || this.model.linkOption === 1 && this.model.selectedLocation.ID);
    }

    private validateLocationModel(): boolean {
        if (this.model) {
            return this.model.linkOption === 0 || (this.model.linkOption === 1 && this.model.selectedLocation.ID != null);
        }
    }

    private validateImageModel() {
        if (this.image) {
            return this.image.data !== null && this.image.data.length > 0 &&
                this.error.imageMinAreaSize === false &&
                this.error.imageMinSize === false &&
                this.error.imageFileSizeMinSize === false &&
                this.error.fileSize === false &&
                this.error.fileType === false;
        }
    }

    private async loadAdvert() {
        this.waiting = true;
        try {
            const response = await this.marketPlaceService.getRetailerAdverts();
            this.model = response.Advert ? this.getClientAdvertModel(response) : this.getDefaultAdvertModel();
            this.backupModel = response.Advert ? this.getClientAdvertModel(response) : this.getDefaultAdvertModel();
            if (this.model.imageID) {
                this.image.src = this.retailerService.getRetailerAdvertsImageUrl(this.model.imageID);
                this.image.id = this.model.imageID;
                this.backupImage.src = this.retailerService.getRetailerAdvertsImageUrl(this.model.imageID);
                this.backupImage.id = this.model.imageID;
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        } finally {
            this.waiting = false;
            this.notificationService.clearAll();
        }
    }

    private getClientAdvertModel(advert: IRetailerAdvert) {

        const retailerAdvert = advert;
        let location: PlaceListShortModel = {};

        if (advert.Advert.Place && advert.Advert.PlaceID) {
            location = {
                ID: advert.Advert.PlaceID,
                Name: advert.Advert.Place.Name,
                Active: true,
                Address: advert.Advert.Place.Address
            };
        } else {
            location = {
                ID: null,
                Name: null,
                Active: null,
                Address: null
            };
        }

        return {
            retailerAdvert,
            selectedLocation: location,
            imageID: advert.Advert.ImageID,
            isNew: false,
            linkOption: advert.Advert.PlaceID ? 1 : 0
        };
    }

    private getDefaultAdvertModel() {

        const retailerAdvert: IRetailerAdvert = {
            Advert: {ID: null, ImageID: null, Place: null, PlaceID: null},
            CurrentOpennings: 0,
            CurrentViews: 0,
            TotalOpennings: 0,
            TotalViews: 0
        };

        const selectedLocation: PlaceListShortModel = {
            ID: null,
            Name: null,
            Active: null,
            Address: null
        };

        return {
            retailerAdvert,
            selectedLocation,
            imageID: null,
            isNew: true,
            linkOption: 0
        };
    }

}

angular.module('naut').component('adForMarketplace', {
    template: require('./ad-for-marketplace.component.html'),
    controller: AdForMarketplaceController,
    controllerAs: 'ctrl'
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/marketplace/ad-for-marketplace/ad-for-marketplace.component.ts